/**
 * The shared furniture every settings section is built from.
 *
 * The old page nested a Card inside each tab panel and repeated the tab's own
 * name in the card title, so every screen carried two borders and two headings
 * before any actual setting. These primitives replace that: one heading per
 * screen, blocks separated by a rule rather than boxed, and a single save
 * affordance that sits in the same place and uses the same verb on every
 * section, so "did that save?" is never a question.
 */
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '~/components/ui/button';

interface PanelHeaderProps {
  title: string;
  blurb: string;
  action?: ReactNode;
}

export function SettingsPanelHeader({ title, blurb, action }: PanelHeaderProps) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-4 pb-6">
      <div className="max-w-[60ch]">
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{blurb}</p>
      </div>
      {action}
    </header>
  );
}

interface BlockProps {
  title: string;
  blurb?: string;
  action?: ReactNode;
  /** Optional: a block can be pure statement of fact with nothing to act on. */
  children?: ReactNode;
}

/**
 * One setting within a section. Tuition, payment schedules, and the enrollment
 * fee are three of these on one page: read together, saved apart.
 */
export function SettingsBlock({ title, blurb, action, children }: BlockProps) {
  return (
    <section className="border-t py-6 first:border-t-0 first:pt-0">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-[62ch]">
          <h2 className="font-medium">{title}</h2>
          {blurb && <p className="mt-1 text-sm text-muted-foreground">{blurb}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

interface SaveBarProps {
  /** Shown only once something has actually been edited. */
  dirty: boolean;
  saving?: boolean;
  error?: string | null;
  onSave: () => void;
  onDiscard?: () => void;
  /** Overrides the default label where a section saves something narrower. */
  label?: string;
}

export function SettingsSaveBar({ dirty, saving, error, onSave, onDiscard, label }: SaveBarProps) {
  if (!dirty && !error) return null;

  return (
    <div
      className="sticky bottom-0 z-10 -mx-4 mt-2 flex flex-wrap items-center justify-between gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6"
      role="status"
    >
      <p className="text-sm text-muted-foreground">
        {error ? <span className="text-destructive">{error}</span> : 'You have unsaved changes.'}
      </p>
      <div className="flex items-center gap-2">
        {onDiscard && (
          <Button type="button" variant="ghost" onClick={onDiscard} disabled={saving}>
            Discard
          </Button>
        )}
        <Button type="button" onClick={onSave} disabled={saving || !dirty}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {saving ? 'Saving…' : label ?? 'Save changes'}
        </Button>
      </div>
    </div>
  );
}

/** Shown where a section has nothing configured yet. An invitation, not a shrug. */
export function SettingsEmpty({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed px-6 py-10 text-center">
      <p className="text-sm text-muted-foreground">{children}</p>
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

/** The skeleton a section shows while the settings query is still in flight. */
export function SettingsLoading() {
  return (
    <div className="animate-pulse space-y-4" aria-label="Loading settings">
      <div className="h-6 w-64 rounded bg-muted" />
      <div className="h-4 w-80 rounded bg-muted" />
      <div className="h-64 rounded bg-muted" />
    </div>
  );
}
